import { useCallback, useEffect, useRef } from "react";

import {
  supportSocketUrl,
  type SupportMessage,
} from "@/services/supportChatService";

/** Backoff between reconnect attempts. Caps so a long outage doesn't spin. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

type Handlers = {
  onMessage: (message: SupportMessage & { conversationId: string }) => void;
  /** Fired after every (re)subscribe so the caller can refetch history. */
  onResync?: () => void;
  /** The other side started typing. Ephemeral — no replay on reconnect. */
  onTyping?: (side: "agent" | "customer") => void;
};

/**
 * Live deltas for one support conversation.
 *
 * The socket is deliberately NOT the source of truth: REST history is.
 * On every (re)connect the caller refetches through `onResync`, so a
 * dropped socket — Railway's edge closing an idle connection, a laptop
 * sleeping — degrades to a poll rather than to silently missing
 * messages. Getting this backwards is how a chat loses a reply and
 * nobody notices until the customer complains.
 */
export function useSupportSocket(
  conversationId: string | null,
  handlers: Handlers
): { sendTyping: () => void } {
  // Keep handlers in a ref so a re-render doesn't tear down the socket.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const socketRef = useRef<(() => WebSocket | null) | null>(null);
  const lastTypingSentAt = useRef(0);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let attempt = 0;
    let disposed = false;

    const connect = (): void => {
      if (disposed) return;
      const url = supportSocketUrl();
      if (!url) return;

      socket = new WebSocket(url);

      socket.onopen = () => {
        attempt = 0;
        socket?.send(
          JSON.stringify({ event: "subscribe", data: { conversationId } })
        );
      };

      socket.onmessage = (raw) => {
        let parsed: { event?: string; data?: unknown };
        try {
          parsed = JSON.parse(String(raw.data)) as typeof parsed;
        } catch {
          return;
        }
        if (parsed.event === "subscribed") {
          handlersRef.current.onResync?.();
          return;
        }
        if (parsed.event === "typing") {
          handlersRef.current.onTyping?.(
            (parsed.data as { side: "agent" | "customer" }).side
          );
          return;
        }
        if (parsed.event === "message") {
          handlersRef.current.onMessage(
            parsed.data as SupportMessage & { conversationId: string }
          );
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        const delay = Math.min(
          RECONNECT_BASE_MS * 2 ** attempt,
          RECONNECT_MAX_MS
        );
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };

      // onerror is followed by onclose; let the close handler retry.
      socket.onerror = () => socket?.close();
    };

    connect();

    socketRef.current = () => socket;

    return () => {
      disposed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [conversationId]);

  /**
   * Debounced to once every 3s. One frame per keystroke would be a flood
   * for a signal whose server-side TTL is 6s — it only has to outlive the
   * gap between typing bursts.
   */
  const sendTyping = useCallback(() => {
    if (!conversationId) return;
    if (Date.now() - lastTypingSentAt.current < 3_000) return;
    const socket = socketRef.current?.();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    lastTypingSentAt.current = Date.now();
    socket.send(JSON.stringify({ event: "typing", data: { conversationId } }));
  }, [conversationId]);

  return { sendTyping };
}
